import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_cafm-restructure/artifacts/o51eebue_597817161_1172388751673908_7998470242494796850_n.jpg";

const navigation = [
  { name: "Home", href: "/" },
  { name: "Chi Siamo", href: "/chi-siamo" },
  { name: "Servizi", href: "/servizi" },
  { name: "U.DI.CON", href: "/udicon" },
  {
    name: "Documenti",
    children: [
      { name: "Documenti 730", href: "/documenti-730" },
      { name: "Documenti ISEE", href: "/documenti-isee" },
    ],
  },
  { name: "News", href: "/news" },
  { name: "Contatti", href: "/contatti" },
];

const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (href) => location.pathname === href;

  return (
    <header className="sticky top-0 z-50 glass-header border-b border-slate-200/50">
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link 
            to="/" 
            className="flex items-center gap-3"
            data-testid="header-logo"
          >
            <img
              src={LOGO_URL}
              alt="CAF Patronato EPAS"
              className="h-14 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden lg:flex items-center gap-1">
            {navigation.map((item) =>
              item.children ? (
                <DropdownMenu key={item.name}>
                  <DropdownMenuTrigger 
                    className="flex items-center gap-1 px-4 py-2 text-base font-medium text-slate-700 hover:text-blue-900 transition-colors duration-200"
                    data-testid={`nav-dropdown-${item.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {item.name}
                    <ChevronDown className="w-4 h-4" />
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="start" className="w-48">
                    {item.children.map((child) => (
                      <DropdownMenuItem key={child.href} asChild>
                        <Link
                          to={child.href}
                          className={`w-full ${isActive(child.href) ? "bg-slate-100" : ""}`}
                          data-testid={`nav-link-${child.href.replace(/\//g, '-').slice(1)}`}
                        >
                          {child.name}
                        </Link>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`px-4 py-2 text-base font-medium transition-colors duration-200 ${
                    isActive(item.href)
                      ? "text-blue-900"
                      : "text-slate-700 hover:text-blue-900"
                  }`}
                  data-testid={`nav-link-${item.href === '/' ? 'home' : item.href.replace(/\//g, '-').slice(1)}`}
                >
                  {item.name}
                </Link>
              )
            )}
          </div>

          {/* CTA Button */}
          <div className="hidden lg:block">
            <Link
              to="/prenota"
              className="inline-flex items-center px-6 py-3 bg-red-600 text-white font-semibold rounded-full hover:bg-red-700 transition-colors duration-200 shadow-lg shadow-red-600/20"
              data-testid="header-cta-prenota"
            >
              Prenota Appuntamento
            </Link>
          </div>

          {/* Mobile menu button */}
          <button
            type="button"
            className="lg:hidden p-2 text-slate-700"
            onClick={() => setMobileMenuOpen(true)}
            data-testid="mobile-menu-button"
          >
            <Menu className="w-6 h-6" />
          </button>
        </div>
      </nav>

      {/* Mobile menu */}
      <div
        className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}
      >
        <div className="flex items-center justify-between p-4 border-b">
          <Link to="/" onClick={() => setMobileMenuOpen(false)}>
            <img
              src={LOGO_URL}
              alt="CAF Patronato EPAS"
              className="h-12 w-auto"
            />
          </Link>
          <button
            type="button"
            className="p-2 text-slate-700"
            onClick={() => setMobileMenuOpen(false)}
            data-testid="mobile-menu-close"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        <div className="p-4 space-y-2">
          {navigation.map((item) =>
            item.children ? (
              <div key={item.name} className="space-y-1">
                <div className="px-4 py-2 text-sm font-semibold text-slate-500 uppercase tracking-wider">
                  {item.name}
                </div>
                {item.children.map((child) => (
                  <Link
                    key={child.href}
                    to={child.href}
                    className={`block px-4 py-3 text-lg font-medium rounded-lg ${
                      isActive(child.href)
                        ? "bg-blue-50 text-blue-900"
                        : "text-slate-700 hover:bg-slate-100"
                    }`}
                    onClick={() => setMobileMenuOpen(false)}
                    data-testid={`mobile-nav-${child.href.replace(/\//g, '-').slice(1)}`}
                  >
                    {child.name}
                  </Link>
                ))}
              </div>
            ) : (
              <Link
                key={item.name}
                to={item.href}
                className={`block px-4 py-3 text-lg font-medium rounded-lg ${
                  isActive(item.href)
                    ? "bg-blue-50 text-blue-900"
                    : "text-slate-700 hover:bg-slate-100"
                }`}
                onClick={() => setMobileMenuOpen(false)}
                data-testid={`mobile-nav-${item.href === '/' ? 'home' : item.href.replace(/\//g, '-').slice(1)}`}
              >
                {item.name}
              </Link>
            )
          )}
          <div className="pt-4">
            <Link
              to="/prenota"
              className="block w-full text-center px-6 py-4 bg-red-600 text-white font-semibold rounded-full hover:bg-red-700 transition-colors duration-200"
              onClick={() => setMobileMenuOpen(false)}
              data-testid="mobile-cta-prenota"
            >
              Prenota Appuntamento
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
